import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";
import { AbstractService } from "../../abstractions/abstract-typed.service";
import { Globals } from "../../app.globals";

@Injectable()
export class ProjectsService extends AbstractService<Project> {
    private apiUrl = "/api/projects";

    constructor(public http: HttpClient, public globals: Globals) {
        super(http, globals);
    }

    getApiUrl() {
        return this.apiUrl;
    }

    getAll(section: string = "general"): Observable<Project[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/getall/" + section).pipe(
            map((res) => {
                var data = res as Project[];

                return data;
            }),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getArchived(section: string = "qs"): Observable<Project[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/getArchived/" + section).pipe(
            map((res) => res as Project[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getWithQaOfflineInfo(): Observable<ProjectQaOfflineInfo[]> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/getQaOfflineInfo/").pipe(
            map((res) => res as ProjectQaOfflineInfo[]),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getWithQaOfflineInfoForProject(
        projectId: number
    ): Observable<ProjectQaOfflineInfo> {
        this.showSpinner();
        return this.http
            .get(this.apiUrl + "/getQaOfflineInfo/" + projectId)
            .pipe(
                map((res) => res as ProjectQaOfflineInfo[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    updateQaOfflineProjects(
        offlineProjects: ProjectQaOfflineInfo[]
    ): Observable<any> {
        this.showSpinner();
        return this.http
            .post(this.apiUrl + "/updateQaOfflineInfo", {
                offlineProjects: offlineProjects,
            })
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    updateQaOfflineProjectAvailability(
        offlineProject: ProjectQaOfflineInfo
    ): Observable<any> {
        this.showSpinner();
        return this.http
            .post(
                this.apiUrl + "/updateProjectOffineAvailability",
                offlineProject
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    get(projectId: number): Observable<Project> {
        this.showSpinner();
        return this.http.get(this.apiUrl + "/" + projectId).pipe(
            map((res) => res as Project),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getSummary(projectId: number, showSpinner: boolean = true): Observable<Project> {
        if (showSpinner)
            this.showSpinner();
        return this.http.get(this.apiUrl + "/summary/" + projectId).pipe(
            map((res) => res as Project),
            catchError(this.handleError),
            finalize(() => {
                if (showSpinner)
                    this.hideSpinner();
            })
        );
    }

    getAllAvailableForTenderingAnalysis(
        tenderId: number = 0,
        section: string = "general"
    ): Observable<Project[]> {
        this.showSpinner();
        return this.http
            .get(
                this.apiUrl +
                    "/getall/availablefortender/" +
                    tenderId +
                    "/" +
                    section
            )
            .pipe(
                map((res) => {
                    var data = res as Project[];
                    return data;
                }),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    add(project: any): Observable<any> {
        this.showSpinner();
        return this.http.post(this.apiUrl, project).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    update(projectId: number, project: any): Observable<any> {
        this.showSpinner();
        return this.http.put(this.apiUrl + "/" + projectId, project).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    updateDeadline(projectId: number, deadline: Date): Observable<any> {
        this.showSpinner();
        return this.http
            .put(this.apiUrl + "/" + projectId + "/deadline", {
                deadline: deadline,
            })
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getNewProjectCode(): Observable<string> {
        return this.http
            .get(this.apiUrl + "/newprojectcode", { responseType: "text" })
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    getOrganizationNameForProject(): Observable<string> {
        return this.http
            .get(this.apiUrl + "/getOrganizationName/" + this.projectId, {
                responseType: "text",
            })
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }
}

export class Project {
    address?: string;
    code: string;
    deadline?: Date;
    description: string;
    endDate: Date;
    enquiryDate: Date;
    hasEdit: boolean;
    id: number;
    loggedByFullName: string;
    logoUrl: string;
    name: string;
    organizationId: number;
    organizationName: string;
    startDate: Date;
    status: string;
    type: string;
    weekHours: number;

    static map(value: any): Project {
        if (!value) return new Project();

        return {
            address: value.address,
            code: value.code,
            deadline: value.deadline,
            description: value.description,
            endDate: value.endDate,
            enquiryDate: value.enquiryDate,
            hasEdit: value.hasEdit,
            id: value.id,
            loggedByFullName: value.loggedByFullName,
            logoUrl: value.logoUrl,
            name: value.name,
            organizationId: value.organizationId,
            organizationName: value.organizationName,
            startDate: value.startDate,
            status: value.status,
            type: value.type,
            weekHours: value.weekHours,
        };
    }
}

export class ProjectQaOfflineInfo {
    isAvailableOffline: boolean;
    lastSynced: string;
    projectId: number;
    projectName: string;
}
